import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { PanelShell } from '../../ui/PanelShell';
import { SectionTitle } from '../../ui/SectionTitle';
import { FieldRow } from '../../ui/FieldRow';
import { NumberField } from '../../ui/NumberField';
import { PillButton } from '../../ui/PillButton';
import { Toggle } from '../../ui/Toggle';
import { TextureSwatch } from './TextureSwatch';
import { textureRows, type TextureVariant } from '../../../data/textures';
type TexturePanelProps = {
  onClose: () => void;
};
export function TexturePanel({
  onClose
}: TexturePanelProps) {
  const [mode, setMode] = useState<'model' | 'face'>('model');
  const [selected, setSelected] = useState<TextureVariant>('blotch');
  const [fileName, setFileName] = useState('');
  const [previewOn, setPreviewOn] = useState(true);
  const [size, setSize] = useState(10);
  const [amplitude, setAmplitude] = useState(0.5);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [seamless, setSeamless] = useState(true);
  const [applied, setApplied] = useState(false);
  return <PanelShell title="Texture" badge={<span className="rounded-[3px] border border-[#f9c9a8] bg-[#fde9dc] px-[5px] py-[1px] text-[10px] font-bold tracking-wide text-[#f06a1d]" data-id="element-659" data-mp="3uy6vz4tlf3j2">BETA</span>} onClose={onClose} footer={<>
          <PillButton disabled={!applied} onClick={() => setApplied(false)} data-id="element-660" data-mp="32885d4zr2yca">
            Reset Last Texture
          </PillButton>
          <PillButton variant="primary" onClick={() => setApplied(true)} data-id="element-661" data-mp="2xbhl05gwxgl2">
            Apply
          </PillButton>
        </>} data-id="element-658" data-mp="3gzyssf5yr5zg">
      
      <SectionTitle info data-id="element-662" data-mp="3782qst0ravpy">Selection Mode</SectionTitle>
      <div role="radiogroup" aria-label="Selection mode" className="grid grid-cols-2 gap-[8px]" data-id="element-663" data-mp="376i05ehkbwxd">
        {(['model', 'face'] as const).map(m => <button key={m} type="button" role="radio" aria-checked={mode === m} onClick={() => setMode(m)} className={`h-[30px] rounded-[6px] border text-[12.5px] capitalize transition-colors duration-150 ${mode === m ? 'border-pf-blue bg-pf-blue-soft text-pf-blue' : 'border-pf-line bg-white text-pf-muted hover:bg-[#fafafa]'}`} data-id="element-664" data-mp="2ejouchpbg7gi">
          
            {m}
          </button>)}
      </div>

      <SectionTitle action={<button type="button" aria-label={previewOn ? 'Hide texture preview' : 'Show texture preview'} onClick={() => setPreviewOn(p => !p)} className="text-[#5b6066] hover:text-pf-ink" data-id="element-666" data-mp="3vnkhalolfu8b">
            {previewOn ? <EyeIcon className="h-[14px] w-[14px]" strokeWidth={2.2} data-id="element-667" data-mp="8htgw33d8f7v" /> : <EyeOffIcon className="h-[14px] w-[14px]" strokeWidth={2.2} data-id="element-668" data-mp="8kfmvyalymnz" />}
          </button>} data-id="element-665" data-mp="w9i5b8d2gjjj">
        
        Displacement Map
      </SectionTitle>
      <label className="flex h-[24px] cursor-pointer overflow-hidden rounded-[4px] border border-pf-line bg-white text-[12px]" data-id="element-669" data-mp="1jwgjfzdprfea">
        <span className={`flex flex-1 items-center truncate px-2 ${fileName ? 'text-pf-ink' : 'text-pf-muted'}`} data-id="element-670" data-mp="1oyfl94tt2494">{fileName || 'Select file...'}</span>
        <span className="flex items-center border-l border-pf-line px-3 text-pf-ink hover:bg-pf-panel" data-id="element-671" data-mp="1u0wxqobjoofz">Browse</span>
        <input type="file" accept="image/*" className="sr-only" onChange={e => setFileName(e.target.files?.[0]?.name ?? '')} data-id="element-672" data-mp="1p95g53ad9t1z" />
      </label>

      <div className="pf-scroll mt-[8px] overflow-x-auto pb-[6px]" data-id="element-673" data-mp="1l720e2go02aw">
        <div className="flex w-max flex-col gap-[6px]" data-id="element-674" data-mp="1gbcyvtl34bby">
          {textureRows.map((row, i) => <div key={i} className="flex gap-[6px]" data-id="element-675" data-mp="1b9454v0e158s" data-mpi={i}>
              {row.map(t => <button key={t.id} type="button" aria-label={t.name} aria-pressed={selected === t.id} onClick={() => setSelected(t.id)} className={`h-[46px] w-[46px] overflow-hidden border-2 transition-colors duration-150 ${selected === t.id ? 'border-pf-blue' : 'border-[#c4c7cb] hover:border-pf-blue-line'}`} data-id="element-676" data-mp="16cnf1v9mogt2">
              
                  <TextureSwatch variant={t.id} data-id="element-677" data-mp="33pf426fncb4r" />
                </button>)}
            </div>)}
        </div>
      </div>

      <SectionTitle data-id="element-678" data-mp="2urbhr7wu7abw">Texture Settings</SectionTitle>
      <FieldRow label="Size" info data-id="element-679" data-mp="2usmmpb20qo4l">
        <NumberField ariaLabel="Texture size" value={size} onChange={setSize} unit="mm" min={1} max={100} step={0.5} className="w-[96px]" data-id="element-680" data-mp="2yrlzfhtzrp6l" />
      </FieldRow>
      <input aria-label="Texture size slider" type="range" className="pf-range my-[8px] px-[4px]" min={1} max={100} step={0.5} value={size} onChange={e => setSize(Number(e.target.value))} data-id="element-681" data-mp="2kzfjnwur3k7o" />
      <FieldRow label="Amplitude" info className="mt-[2px]" data-id="element-682" data-mp="2b4duj10h6i53">
        <NumberField ariaLabel="Texture amplitude" value={amplitude} onChange={setAmplitude} unit="mm" min={0.05} max={5} step={0.05} className="w-[96px]" data-id="element-683" data-mp="2g0vwxu8c24e9" />
      </FieldRow>
      <input aria-label="Amplitude slider" type="range" className="pf-range my-[8px] px-[4px]" min={0.05} max={5} step={0.05} value={amplitude} onChange={e => setAmplitude(Number(e.target.value))} data-id="element-684" data-mp="270zb00dbvjmw" />

      <div className="-mx-2 mt-[10px] border-t border-pf-line" data-id="element-685" data-mp="1igcgms6pobex">
        <button type="button" aria-expanded={advancedOpen} onClick={() => setAdvancedOpen(o => !o)} className="flex h-[38px] w-full items-center justify-between px-2 text-[13.5px] font-bold text-pf-ink" data-id="element-686" data-mp="1ihxasgp45sc9">
          
          Advanced Settings
          <svg viewBox="0 0 5 8" className={`h-[7px] w-[5px] transition-transform duration-150 ${advancedOpen ? 'rotate-90' : ''}`} fill="currentColor" data-id="element-687" data-mp="26cgt043pwtu0">
            <path d="M0 0 L5 4 L0 8 Z" data-id="element-688" data-mp="2g3l5mx5x82lo" />
          </svg>
        </button>
        <AnimatePresence initial={false} data-id="element-689" data-mp="2g5nwtkit81rz">
          {advancedOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="overflow-hidden px-2" data-id="element-690" data-mp="2b59zoxiqyc8q">
            
              <FieldRow label="Rotation" info data-id="element-691" data-mp="1htlc59h7gsqw">
                <NumberField ariaLabel="Texture rotation" value={0} decimals={0} step={5} max={360} unit="°" className="w-[96px]" data-id="element-692" data-mp="1mqvbbguecirx" />
              </FieldRow>
              <FieldRow label="Seamless Blending" info data-id="element-693" data-mp="1qr5cmg78ul0g">
                <Toggle label="Seamless blending" checked={seamless} onChange={setSeamless} data-id="element-694" data-mp="1vjymtjlheljx" />
              </FieldRow>
            </motion.div>}
        </AnimatePresence>
      </div>
    </PanelShell>;
}